import { GridColDef } from '@mui/x-data-grid-pro';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import PersonalContactsGridToolbar from './personal-contacts-grid-toolbar';
import { ContactGroup, Person } from '../../../../../types';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import AddContactDialog from './add-personal-contact';
import MoveContactsDialog from './move-contacts-dialog';
import DeleteContactsDialog from './delete-personal-contact';
import EditContactDialog from './edit-personal-contact';
import SharedErrorBoundary from '../../../../shared/shared-error-boundary';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridServer from '../../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';
import Contacts from '@mui/icons-material/Contacts';

const columns: GridColDef[] = [
  {
    field: 'contactName',
    headerName: 'Contact Name',
    editable: false,
    sortable: false,
    filterable: false,
    flex: 1,
  },
  {
    field: 'email',
    headerName: 'Email',
    editable: false,
    sortable: false,
    filterable: false,
    flex: 2,
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone Number',
    editable: false,
    sortable: false,
    filterable: false,
    flex: 1,
  },
  {
    field: 'organization',
    headerName: 'Organization',
    editable: false,
    sortable: false,
    filterable: false,
    flex: 1,
  },
  {
    field: 'address',
    headerName: 'Address',
    flex: 1,
    editable: false,
    sortable: false,
    filterable: false,
  },
];

interface GridContactData {
  id: string;
  etag: string;
  contactName: string;
  email: string;
  phoneNumber: string | undefined;
  organization: string | undefined;
  address: string | undefined;
}

interface PersonalContactsGridProps {
  userEmail: string;
  userView: boolean;
  userRoles: string[];
  changeLogRoles: string[];
  isLoading: boolean;
  isExporting: boolean;
  personData: Person[] | undefined;
  contactGroups: ContactGroup[] | undefined;
  groupMembers: string[] | undefined;
  handleMutate: () => void;
  selectedLabel: string;
  paginationModel: {
    pageSize: number;
    page: number;
  };
  setPaginationModel: Dispatch<
    SetStateAction<{
      pageSize: number;
      page: number;
    }>
  >;
  selectedContact: Person | null;
  setSelectedContact: Dispatch<SetStateAction<Person | null>>;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
}

export default function PersonalContactsGrid({
  userEmail,
  userView,
  userRoles,
  changeLogRoles,
  isLoading,
  isExporting,
  personData,
  contactGroups,
  groupMembers,
  handleMutate,
  selectedLabel,
  paginationModel,
  setPaginationModel,
  selectedContact,
  setSelectedContact,
  selectedRows,
  setSelectedRows,
  searchText,
  setSearchText,
}: PersonalContactsGridProps) {
  const [openDeleteContacts, setOpenDeleteContacts] = useState(false);
  const [openAddContact, setOpenAddContact] = useState(false);
  const [openEditContact, setOpenEditContact] = useState(false);
  const [openMoveContacts, setOpenMoveContacts] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  function getContactName(person: Person) {
    const name = person.names?.[0];
    return (
      (name?.givenName ?? '') +
        (name?.familyName ? ` ${name.familyName}` : '') ||
      name?.middleName ||
      ''
    );
  }

  function getEmail(person: Person) {
    return person.emailAddresses?.[0]?.value || '';
  }

  function getPhoneNumber(person: Person) {
    return (
      person.phoneNumbers?.find((phoneNumber) => phoneNumber?.metadata?.primary)
        ?.value ?? person.phoneNumbers?.[0]?.value
    );
  }

  function getOrganization(person: Person) {
    const organization = person.organizations?.[0];

    return (
      organization?.name ||
      organization?.department ||
      organization?.title ||
      ''
    );
  }

  function getAddress(person: Person) {
    return person.addresses?.[0]?.formattedValue ?? '';
  }

  const personalContactsData = useMemo<GridContactData[]>(() => {
    if (!personData) return [];

    return personData.flatMap((person) => {
      const resourceName = person?.resourceName;

      if (!resourceName) return [];

      return [
        {
          id: resourceName,
          etag: person.etag || '',
          contactName: getContactName(person),
          email: getEmail(person),
          phoneNumber: getPhoneNumber(person),
          organization: getOrganization(person),
          address: getAddress(person),
        },
      ];
    });
  }, [personData]);

  const toolbarProps = useMemo(
    () => ({
      userEmail,
      selectedLabel,
      selectedRows,
      userView,
      userRoles,
      setOpenAddContact,
      setOpenDeleteContacts,
      setOpenMoveContacts,
      isLoading,
      isExporting,
      handleMutate,
    }),
    [
      handleMutate,
      isExporting,
      isLoading,
      selectedLabel,
      selectedRows,
      userEmail,
      userRoles,
      userView,
    ]
  );

  const CustomToolbar = useToolbar(PersonalContactsGridToolbar, toolbarProps);

  return (
    <>
      <SharedDataGridServer
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays(
            'Personal Contacts',
            <Contacts />,
            searchText !== '' ? 'Try updating your search' : ''
          ),
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        checkboxSelection
        rows={personalContactsData || []}
        columns={columns}
        loading={isLoading}
        rowCount={
          searchText !== '' && !isLoading
            ? personData?.length
            : groupMembers && groupMembers.length
            ? groupMembers.length
            : 0
        }
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({ page: 0, pageSize: newModel.pageSize });
          } else {
            setPaginationModel({ ...newModel });
          }
        }}
        onFilterModelChange={(newModel) => {
          const search = newModel.quickFilterValues?.join(' ') ?? '';
          setSearchText(search);
        }}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        onRowClick={(row) => {
          if (!personData) return;
          const selectedContact = personData.find(
            (person) => person.resourceName === row.row.id
          )!;
          setSelectedContact(selectedContact);
          setOpenEditContact(true);
        }}
      />
      <SharedErrorBoundary section="User Management - Personal Contacts: Grid Dialogs">
        {openAddContact ? (
          <AddContactDialog
            open={openAddContact}
            handleClose={() => setOpenAddContact(false)}
            userEmail={userEmail}
            userView={userView}
            selectedLabel={selectedLabel}
            contactList={personData || []}
            groupMembers={groupMembers || []}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
        {openDeleteContacts && contactGroups ? (
          <DeleteContactsDialog
            open={openDeleteContacts}
            handleClose={() => setOpenDeleteContacts(false)}
            userEmail={userEmail}
            userView={userView}
            contactList={personData || []}
            selectedLabel={selectedLabel}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            groupMembers={groupMembers || []}
            labelList={contactGroups}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
        {openMoveContacts && contactGroups ? (
          <MoveContactsDialog
            open={openMoveContacts}
            handleClose={() => setOpenMoveContacts(false)}
            userEmail={userEmail}
            userView={userView}
            contactList={personData || []}
            selectedLabel={selectedLabel}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            labels={contactGroups}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
        {openEditContact && selectedContact ? (
          <EditContactDialog
            open={openEditContact}
            handleClose={() => setOpenEditContact(false)}
            userEmail={userEmail}
            userView={userView}
            userRoles={userRoles}
            contactList={personData || []}
            selectedContact={selectedContact}
            selectedLabel={selectedLabel}
            groupMembers={groupMembers || []}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
        {openChangeLogDialog ? (
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={handleMutate}
            mutateLoading={isExporting}
          />
        ) : null}
      </SharedErrorBoundary>
    </>
  );
}
